import { useCallback, useEffect, useRef, useState } from "react";

import {
  acquireSingleTrack,
  getAudioConstraint,
  getStartStrategy,
  getTrackEnabledState,
  getVideoConstraint,
  removeAndStopTracks,
  replaceStreamTrack,
  stopAllTracks,
} from "./mediaStreamUtils";
import type {
  CameraStatus,
  MediaInputDevice,
  PermissionState,
  StreamStatus,
} from "./mediaTypes";
import { useMediaDevices } from "./useMediaDevices";

export interface StartCameraOptions {
  cameraDeviceId?: string | null;
  micDeviceId?: string | null;
}

interface UseCameraStreamReturn {
  cameraDevices: MediaInputDevice[];
  cameraStream: MediaStream | null;
  cleanup: () => void;
  error: Error | null;
  isCameraEnabled: boolean;
  isMicEnabled: boolean;
  micDevices: MediaInputDevice[];
  permissionState: PermissionState;
  startCamera: (options?: StartCameraOptions) => Promise<void>;
  status: StreamStatus;
  stopCamera: () => void;
  toggleCamera: () => void;
  toggleMic: () => void;
}

export const useCameraStream = (): UseCameraStreamReturn => {
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const isCameraRequestingRef = useRef(false);
  const selectedCameraDeviceIdRef = useRef<string | null>(null);
  const selectedMicDeviceIdRef = useRef<string | null>(null);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>({
    status: "idle",
  });
  const [isCameraEnabled, setIsCameraEnabled] = useState(false);
  const [isMicEnabled, setIsMicEnabled] = useState(false);
  const { cameraDevices, micDevices, refreshDevices } = useMediaDevices();

  const syncTrackStates = useCallback((stream: MediaStream | null) => {
    const { isCameraEnabled, isMicEnabled } = getTrackEnabledState(stream);
    setIsCameraEnabled(isCameraEnabled);
    setIsMicEnabled(isMicEnabled);
  }, []);

  // After mutating cameraStreamRef.current in place, wrap the updated tracks in
  // a new MediaStream so consumers that depend on stream identity (e.g.
  // useCompositedRecordingStream) re-run and pick up the new track.
  const commitUpdatedStream = useCallback(() => {
    if (!cameraStreamRef.current) return;
    const updatedStream = new MediaStream(cameraStreamRef.current.getTracks());
    cameraStreamRef.current = updatedStream;
    setCameraStream(updatedStream);
    syncTrackStates(updatedStream);
    setCameraStatus({ status: "active", permissionState: "granted" });
  }, [syncTrackStates]);

  const replaceMicTrack = useCallback(
    async (micDeviceId: string | null) => {
      if (!cameraStreamRef.current) return;
      const nextAudioTrack = await acquireSingleTrack(
        { audio: getAudioConstraint(micDeviceId), video: false },
        "audio",
        "Could not access selected microphone",
      );
      replaceStreamTrack(cameraStreamRef.current, "audio", nextAudioTrack);
      selectedMicDeviceIdRef.current = micDeviceId;
      syncTrackStates(cameraStreamRef.current);
      setCameraStatus({ status: "active", permissionState: "granted" });
    },
    [syncTrackStates],
  );

  const replaceCameraTrack = useCallback(
    async (cameraDeviceId: string | null) => {
      if (!cameraStreamRef.current) return;
      const nextVideoTrack = await acquireSingleTrack(
        { audio: false, video: getVideoConstraint(cameraDeviceId) },
        "video",
        "Could not access selected camera",
      );
      replaceStreamTrack(cameraStreamRef.current, "video", nextVideoTrack);
      selectedCameraDeviceIdRef.current = cameraDeviceId;
      commitUpdatedStream();
    },
    [commitUpdatedStream],
  );

  const startStream = useCallback(
    async (cameraDeviceId: string | null, micDeviceId: string | null) => {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: getAudioConstraint(micDeviceId),
        video: getVideoConstraint(cameraDeviceId),
      });
      cameraStreamRef.current = stream;
      selectedCameraDeviceIdRef.current = cameraDeviceId;
      selectedMicDeviceIdRef.current = micDeviceId;
      setCameraStream(stream);
      syncTrackStates(stream);
      setCameraStatus({ status: "active", permissionState: "granted" });
      await refreshDevices();
    },
    [refreshDevices, syncTrackStates],
  );

  const startCamera = useCallback(
    async (options: StartCameraOptions = {}) => {
      if (isCameraRequestingRef.current) return;
      isCameraRequestingRef.current = true;
      setCameraStatus({ status: "requesting" });

      const cameraDeviceId = options.cameraDeviceId ?? null;
      const micDeviceId = options.micDeviceId ?? null;
      const hasCameraChanged =
        cameraDeviceId !== selectedCameraDeviceIdRef.current;
      const hasMicChanged = micDeviceId !== selectedMicDeviceIdRef.current;
      const strategy = getStartStrategy(
        Boolean(cameraStreamRef.current),
        hasCameraChanged,
        hasMicChanged,
      );

      try {
        switch (strategy) {
          case "no-op":
            syncTrackStates(cameraStreamRef.current);
            setCameraStatus({ status: "active", permissionState: "granted" });
            break;

          case "replace-mic":
            await replaceMicTrack(micDeviceId);
            await refreshDevices();
            break;

          case "replace-camera":
            await replaceCameraTrack(cameraDeviceId);
            await refreshDevices();
            break;

          case "full-restart": {
            if (cameraStreamRef.current) {
              stopAllTracks(cameraStreamRef.current);
              cameraStreamRef.current = null;
            }
            await startStream(cameraDeviceId, micDeviceId);
            break;
          }
        }
      } catch (err) {
        const mediaError = err as Error;

        if (
          mediaError.name === "OverconstrainedError" &&
          (cameraDeviceId !== null || micDeviceId !== null)
        ) {
          try {
            await startStream(null, null);
            return;
          } catch {
            // Fall through to generic error handling below
          }
        }

        setCameraStatus({
          status: "error",
          error: mediaError,
          permissionState:
            mediaError.name === "NotAllowedError" ? "denied" : null,
        });
      } finally {
        isCameraRequestingRef.current = false;
      }
    },
    [
      refreshDevices,
      replaceCameraTrack,
      replaceMicTrack,
      startStream,
      syncTrackStates,
    ],
  );

  const cleanup = useCallback(() => {
    stopAllTracks(cameraStreamRef.current);
    cameraStreamRef.current = null;
    setCameraStream(null);
    setIsCameraEnabled(false);
    setIsMicEnabled(false);
    setCameraStatus({ status: "idle" });
  }, []);

  const toggleTrack = useCallback(
    async (
      kind: "audio" | "video",
      replaceTrack: (deviceId: string | null) => Promise<void>,
      deviceId: string | null,
    ) => {
      if (!cameraStreamRef.current) return;
      const hasTracks =
        kind === "video"
          ? cameraStreamRef.current.getVideoTracks().length > 0
          : cameraStreamRef.current.getAudioTracks().length > 0;

      if (hasTracks) {
        removeAndStopTracks(cameraStreamRef.current, kind);
        if (kind === "video") {
          commitUpdatedStream();
        } else {
          syncTrackStates(cameraStreamRef.current);
        }
      } else {
        setCameraStatus({ status: "requesting" });
        try {
          await replaceTrack(deviceId);
        } catch (err) {
          const mediaError = err as Error;
          setCameraStatus({
            status: "error",
            error: mediaError,
            permissionState:
              mediaError.name === "NotAllowedError" ? "denied" : null,
          });
        }
      }
    },
    [commitUpdatedStream, syncTrackStates],
  );

  const toggleCamera = useCallback(
    () =>
      void toggleTrack(
        "video",
        replaceCameraTrack,
        selectedCameraDeviceIdRef.current,
      ),
    [toggleTrack, replaceCameraTrack],
  );

  const toggleMic = useCallback(
    () =>
      void toggleTrack(
        "audio",
        replaceMicTrack,
        selectedMicDeviceIdRef.current,
      ),
    [toggleTrack, replaceMicTrack],
  );

  useEffect(() => cleanup, [cleanup]);

  const status: StreamStatus = cameraStatus.status;
  const error: Error | null =
    cameraStatus.status === "error" ? cameraStatus.error : null;
  const permissionState: PermissionState =
    cameraStatus.status === "active" || cameraStatus.status === "error"
      ? cameraStatus.permissionState
      : null;

  return {
    cameraDevices,
    cameraStream,
    cleanup,
    error,
    isCameraEnabled,
    isMicEnabled,
    micDevices,
    permissionState,
    startCamera,
    status,
    stopCamera: cleanup,
    toggleCamera,
    toggleMic,
  };
};
